import Card from "../../../components/Card/Card";
import { t } from "i18next";
import { useEffect, useState } from "react";
import NotificationsAPI, { Alerts } from "../../../api/AlertsAPI";
import AlertEmailField from "../../../components/AlertEmailField/AlertEmailField";
import PrimaryButton from "../../../components/PrimaryButton/PrimaryButton";
import { PulseLoader } from "react-spinners";
import Subtitle from "../../../components/Subtitle/Subtitle";

type AlertConfig = {
  enabled: boolean;
  emails: string[];
  translationKey: string;
};

type AlertKeys = keyof typeof predefinedAlerts;

interface AlertsWithIndex extends Alerts {
  [key: string]: {
    enabled: boolean;
    emails: string[];
  };
}

const predefinedAlerts: Record<keyof Alerts, AlertConfig> = {
  pendingBookingAdded: { enabled: false, emails: [], translationKey: "alerts.option.pending_booking_added" },
  pendingOrderAdded: { enabled: false, emails: [], translationKey: "alerts.option.pending_order_added" },
  orderAdded: { enabled: false, emails: [], translationKey: "alerts.option.order_added" },
  removalMeeting: { enabled: false, emails: [], translationKey: "alerts.option.removal_meeting" },
  contractSigned: { enabled: false, emails: [], translationKey: "alerts.option.contract_signed" },
  lowStock: { enabled: false, emails: [], translationKey: "alerts.option.low_stock" },
  commentsOnOrder: { enabled: false, emails: [], translationKey: "alerts.option.comments_on_order" },
  catalogImported: { enabled: false, emails: [], translationKey: "alerts.option.catalog_imported" },
  secondHandComments: { enabled: false, emails: [], translationKey: "alerts.option.second_hand_comments" },
  secondHandDressSubmitted: { enabled: false, emails: [], translationKey: "alerts.option.second_hand_dress_submitted" }
};

function AlertsSegment() {
  const [isLoading, setIsLoading] = useState(false);
  const [alertsData, setAlertsData] = useState<Record<AlertKeys, AlertConfig>>(predefinedAlerts);

  useEffect(() => {
    setIsLoading(true);
    const loadInitialData = async () => {
      try {
        const fetched = await NotificationsAPI.get();
        const updatedAlerts: Record<AlertKeys, AlertConfig> = { ...predefinedAlerts };
        (Object.keys(predefinedAlerts) as Array<AlertKeys>).forEach((key) => {
          if (fetched.data[key]) {
            updatedAlerts[key] = {
              ...updatedAlerts[key],
              ...fetched.data[key],
            };
          }
        });
        setAlertsData(updatedAlerts);
      } catch (error) {
        console.error("Failed to fetch initial alerts data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, []);

  const updateAlertData = (alertName: AlertKeys, data: { enabled: boolean; emails: string[] }) => {
    setAlertsData((prev) => ({
      ...prev,
      [alertName]: { ...prev[alertName], ...data },
    }));
  };

  const saveAllAlerts = async () => {
    setIsLoading(true);
    try {
      const alertsToUpdate: AlertsWithIndex = Object.keys(predefinedAlerts).reduce((acc, key) => {
        const typedKey = key as AlertKeys;
        acc[typedKey] = alertsData[typedKey];
        return acc;
      }, {} as AlertsWithIndex);
      await NotificationsAPI.update(alertsToUpdate);
    } catch (error) {
      console.error("Failed to update alerts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Grouping keys for sections
  const storeAlerts: AlertKeys[] = [
    "pendingBookingAdded",
    "removalMeeting",
    "contractSigned"
  ];
  const orderAlerts: AlertKeys[] = [
    "catalogImported",
    "pendingOrderAdded",
    "commentsOnOrder",
    "orderAdded",
    "lowStock",
  ];
  const secondHandAlerts: AlertKeys[] = [
    "secondHandDressSubmitted",
    "secondHandComments"
  ];

  return <div className="space-y-6">
    <div>
      <Subtitle text={t("alerts.section.store")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {storeAlerts.map((key) => (
          <Card key={key}>
            <AlertEmailField
              isDisabled={isLoading}
              label={t("alerts.emails")!}
              title={t(alertsData[key].translationKey)}
              initialEmails={alertsData[key].emails}
              initialIsEnabled={alertsData[key].enabled}
              onUpdate={(data) => updateAlertData(key, data)}
            />
          </Card>
        ))}
      </div>
    </div>
    <div>
      <Subtitle text={t("alerts.section.orders")}/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {orderAlerts.map((key) => (
          <Card key={key}>
            <AlertEmailField
              isDisabled={isLoading}
              label={t("alerts.emails")!}
              title={t(alertsData[key].translationKey)}
              initialEmails={alertsData[key].emails}
              initialIsEnabled={alertsData[key].enabled}
              onUpdate={(data) => updateAlertData(key, data)}
            />
          </Card>
        ))}
      </div>
    </div>
    <div>
      <Subtitle text={t("alerts.section.second_hand")}/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {secondHandAlerts.map((key) => (
          <Card key={key}>
            <AlertEmailField
              isDisabled={isLoading}
              label={t("alerts.emails")!}
              title={t(alertsData[key].translationKey)}
              initialEmails={alertsData[key].emails}
              initialIsEnabled={alertsData[key].enabled}
              onUpdate={(data) => updateAlertData(key, data)}
            />
          </Card>
        ))}
      </div>
    </div>
    <div className="flex justify-center">
      { isLoading ?
        <PulseLoader color="#fdd1d1" /> :
        <PrimaryButton text={t("global.save")!} fn={saveAllAlerts} isDisabled={isLoading} />
      }
    </div>
  </div>
}

export default AlertsSegment;